import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Center, Heading, Spinner, Flex, VStack } from '@chakra-ui/react';
import { useEducationContext } from '@src/components/sco/Education/EducationContext';
import { EducationErrorState } from '@src/components/sco/Education/EducationErrorState';
import { VideoLessonProgressBar } from '@src/components/sco/Education/VideoListDetail/VideoLessonProgressBar';
import { VideoLessonsList } from '@src/components/sco/Education/VideoListDetail/VideoLessonsList';
import { ViewableTracker } from '@src/components/sco/ViewableTracker';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
type VideoListDetailProps = {
  videoListSlug?: string;
};

/**
 * Resolve a lone `/videos/{slug}` that is not a VideoList:
 * - Video in a series → `/videos/{listSlug}/{videoSlug}`
 * - Standalone video → `/watch/{slug}`
 */
function useVideoSlugFallbackRedirect(args) {
  const $ = _c(12);
  const router = useRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = VideoListDetail.queries.videoBySlug();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== args.slug) {
    t1 = {
      videoSlug: args.slug
    };
    $[1] = args.slug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !args.enabled || !args.slug;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      variables: t1,
      skip: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const videoQuery = useQuery(t0, t3);
  const video = videoQuery.data?.video;
  const listSlug = video?.videoLists?.[0]?.slug;
  const redirectHref = video?.slug ? listSlug ? `/videos/${listSlug}/${video.slug}` : `/watch/${video.slug}` : null;
  let t4;
  let t5;
  if ($[6] !== redirectHref || $[7] !== router) {
    t4 = () => {
      if (redirectHref) {
        router.replace(redirectHref);
      }
    };
    t5 = [redirectHref, router];
    $[6] = redirectHref;
    $[7] = router;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  const t6 = args.enabled && (videoQuery.loading || !!redirectHref);
  let t7;
  if ($[10] !== t6) {
    t7 = {
      loading: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export const VideoListDetail = props => {
  const $ = _c(34);
  const educationContext = useEducationContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = VideoListDetail.queries.videoList();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.videoListSlug) {
    t1 = {
      variables: {
        videoListSlug: props.videoListSlug
      }
    };
    $[1] = props.videoListSlug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const videoListQuery = useQuery(t0, t1);
  const videoList = videoListQuery.data?.videoList;
  const videoListMissing = !videoListQuery.loading && !videoListQuery.error && !videoListQuery.data?.videoList;
  let t2;
  if ($[3] !== props.videoListSlug || $[4] !== videoListMissing) {
    t2 = {
      enabled: videoListMissing,
      slug: props.videoListSlug
    };
    $[3] = props.videoListSlug;
    $[4] = videoListMissing;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const videoFallback = useVideoSlugFallbackRedirect(t2);
  let t3;
  if ($[6] !== educationContext || $[7] !== videoList) {
    t3 = () => {
      if (videoList) {
        educationContext.setActiveContentItem(videoList);
        educationContext.setBreadcrumbs([{
          title: "Videos",
          href: "/videos"
        }, {
          title: videoList?.title,
          href: `/videos/${videoList?.slug}`
        }]);
      }
    };
    $[6] = educationContext;
    $[7] = videoList;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = videoList?.id;
  const t5 = videoList?.slug;
  const t6 = videoList?.title;
  let t7;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = [t4, t5, t6];
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  useUniversalEffect(t3, t7);
  if (videoList) {
    let t8;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = ["20px", "20px", "20px", "20px", "40px"];
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== videoList.__typename || $[15] !== videoList.id) {
      t9 = <ViewableTracker viewableId={videoList.id} viewableType={videoList.__typename} />;
      $[14] = videoList.__typename;
      $[15] = videoList.id;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = ["2xl", "4xl"];
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    const t11 = `/videos/${videoList.slug}`;
    let t12;
    if ($[18] !== t11 || $[19] !== videoList.title) {
      t12 = <Heading size={t10}><NextLink href={t11}>{videoList.title}</NextLink></Heading>;
      $[18] = t11;
      $[19] = videoList.title;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    let t13;
    if ($[21] !== videoList) {
      t13 = <VideoLessonsList videoList={videoList} videos={videoList.videos} />;
      $[21] = videoList;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== t12 || $[24] !== t13 || $[25] !== t9) {
      t14 = <Flex align="center" direction="column" gap={0} justify="center" p={t8} width="full"><VStack align="flex-start" gap={8} maxWidth="container.lg" width="full">{t9}{t12}{t13}</VStack></Flex>;
      $[23] = t12;
      $[24] = t13;
      $[25] = t9;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    return t14;
  }
  if (videoListQuery.error) {
    let t8;
    if ($[27] !== videoListQuery.error) {
      t8 = videoListQuery.error.toString();
      $[27] = videoListQuery.error;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    let t9;
    if ($[29] !== t8) {
      t9 = <Center padding="2em">{t8}</Center>;
      $[29] = t8;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    return t9;
  }
  if (videoListMissing) {
    if (videoFallback.loading) {
      let t8;
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <Center padding="2em"><Spinner /></Center>;
        $[31] = t8;
      } else {
        t8 = $[31];
      }
      return t8;
    }
    let t8;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <EducationErrorState ctaHref="/videos" ctaLabel="Explore all Videos" headingText="Video Series Not Found." />;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    return t8;
  }
  let t8;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Center padding="2em"><Spinner /></Center>;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  return t8;
};
VideoListDetail.queries = {
  videoList: () => gql`
    query ($videoListSlug: String) {
      videoList(where: { slug: $videoListSlug }) {
        id
        featuredImage {
          id
          url(transformation: { image: { resize: { fit: max, width: 1000 } } })
        }
        slug
        title
        shortDescription
        people {
          id
          firstName
          lastName
          email
        }
        videos {
          ${VideoLessonProgressBar.fragments.Video()}
          id
          slug
          featuredImage {
            id
            url
          }
          title
          subtitle
          transcript
          people {
            id
            firstName
            lastName
            shortBio
            avatar {
              id
              url
            }
          }
          longDescription
          shortDescription
          videoUrl
        }
      }
    }
  `,
  videoBySlug: () => gql`
    query ($videoSlug: String) {
      video(where: { slug: $videoSlug }) {
        id
        slug
        videoLists {
          id
          slug
        }
      }
    }
  `
};